import { useMemo, useCallback, useRef, type PointerEvent, type FC } from "react";
import { type ComponentProps } from "react";
import { cn } from "@/lib/utils";
import { useAudioRecorder } from "../../hooks/use-audio-recorder";
import { Button } from "@/components/ui/button";
import { Mic, Square } from "lucide-react";

const MIC_STOP_DELAY_MS = 250;

// ## 1. Define Prop Types
// Create a type for the component's props to ensure type safety.
// It extends the base props of the underlying `Button` component but omits
// the event handlers that are managed internally to prevent conflicts.
type MicButtonProps = Omit<ComponentProps<typeof Button>, 'onPointerDown' | 'onPointerUp' | 'onPointerLeave'> & {
  label?: string;
  onRecordingComplete: (blob: Blob) => void;
  onRecordingStarted?: () => void;
  onRecordingEnded?: () => void;
  onError?: (error: Error) => void;
};

// ## 2. Type the Component with `React.FC`
// Declare the component as a Functional Component (`FC`) with the defined props.
export const MicButton: FC<MicButtonProps> = ({
  className,
  label,
  onRecordingComplete,
  onRecordingStarted,
  onRecordingEnded,
  onError,
  ...props
}) => {
  const { 
    isRecording, 
    startRecording, 
    stopRecording, 
    isInitializing,
    duration,
  } = useAudioRecorder({
    onRecordingComplete,
    onRecordingStarted,
    onRecordingStopped: onRecordingEnded, // Map prop to hook's name
    onError,
  });

  const timerLabel = useMemo(() => {
    const totalSeconds = Math.floor(duration / 1000);
    const mins = String(Math.floor(totalSeconds / 60)).padStart(2, "0");
    const secs = String(totalSeconds % 60).padStart(2, "0");
    return `${mins}:${secs}`;
  }, [duration]);
  
  // ## 3. Type the Ref
  // The ref will hold the timer ID from `setTimeout`, which is of type `NodeJS.Timeout`.
  const stopTimerRef = useRef<NodeJS.Timeout | null>(null);

  // ## 4. Type Event Handlers
  // Explicitly type the event object for pointer events.
  const handlePointerDown = useCallback((e: PointerEvent<HTMLButtonElement>) => {
    if (e.isPrimary && !isRecording && !isInitializing) {
      e.preventDefault();
      if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
      startRecording();
    }
  }, [isRecording, isInitializing, startRecording]);

  const handlePointerUp = useCallback(() => {
    if (isRecording) {
      stopTimerRef.current = setTimeout(() => stopRecording(), MIC_STOP_DELAY_MS);
    }
  }, [isRecording, stopRecording]);

  const handlePointerLeave = useCallback(() => {
    if (isRecording) stopRecording();
  }, [isRecording, stopRecording]);

  return (
    <Button
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerLeave}
      onContextMenu={(e) => e.preventDefault()}
      disabled={isInitializing}
      variant={isRecording ? "destructive" : "default"}
      className={cn(
        isRecording && "animate-pulse",
        "select-none touch-none",
        className
      )}
      style={{ touchAction: 'none' }}
      {...props}
    >
      {isRecording ? (
        <>
          <Square className="h-5 w-5 fill-current mr-2" />
          <span>{timerLabel}</span>
        </>
      ) : (
        <>
          <Mic className="h-5 w-5 mr-2" />
          {label || "Hold to Record"}
        </>
      )}
    </Button>
  );
};